/**
 * Novinky v aplikaci — **jediný zdroj pravdy** pro stránku `/novinky`
 * i pro root `CHANGELOG.md` (ten se z tohoto souboru generuje:
 * `node scripts/gen-changelog.mjs`).
 *
 * Pravidlo: každá změna, která je vidět v UI nebo mění chování aplikace, dostane
 * nový záznam nahoře — jazykem uživatele („co je nově vidět a k čemu to je"),
 * ne výpis commitů a ne názvy souborů/tříd. Provozní detaily (nasazení,
 * konfigurace, migrace, oprávnění) patří do `adminNote` — v UI je vidí jen admin.
 */

export type ChangeKind = 'novinka' | 'vylepseni' | 'oprava';

export interface ChangeItem {
  kind: ChangeKind;
  /** Krátký titulek — to, co si uživatel odnese. */
  title: string;
  /** Doplnění: k čemu to je, kde to najde. */
  detail?: string;
}

export interface ChangeEntry {
  /** ISO datum (YYYY-MM-DD) — řadí se podle něj, nejnovější první. */
  date: string;
  /** Souhrnný nadpis vydání. */
  title: string;
  items: ChangeItem[];
  /** Poznámka pro správce (nasazení, konfigurace) — v UI vizuálně odlišená. */
  adminNote?: string;
}

export const KIND_LABEL: Record<ChangeKind, string> = {
  novinka: 'Nové',
  vylepseni: 'Vylepšení',
  oprava: 'Oprava',
};

export const CHANGELOG: ChangeEntry[] = [
  {
    date: '2026-09-30',
    title: 'Méně zbytečného schvalování, rychlé dopisování materiálu',
    items: [
      {
        kind: 'vylepseni',
        title: 'Nové schválení je potřeba jen při změně popisu činnosti',
        detail:
          'Materiál, výkon, úkony, zjištění, kontrolu i stav položky lze zapisovat a opravovat ' +
          'i po uzavření dne — bez „Otevřít k úpravě" a bez nového schvalování. U schválené ' +
          'operativy jde také rovnou změnit mechanizaci a zaměstnance položky (tužka u položky) ' +
          'a poznámku operativy. Znovu schvalovat se musí jen změna popisu činnosti nebo přidání ' +
          'či smazání položky.',
      },
      {
        kind: 'novinka',
        title: 'Materiál: rozpis po dnech a rychlé dopsání k libovolnému dni',
        detail:
          'Na stránce Materiál je pod součty nový rozpis po dnech — u každého dne vidíte, jaký ' +
          'materiál se ke které položce zapsal. Tlačítkem „Přidat" u dne (nebo „Zapsat materiál" ' +
          'nahoře) otevřete okno, vyberete položku a napíšete řádky Co + Kolik; Enter v poli ' +
          'Kolik přidá další řádek, „Uložit a další" nechá okno otevřené pro další zápis. ' +
          'Hodí se pro zpětné dopisování z papírových reportů.',
      },
    ],
    adminNote:
      'Report (ReportPolicy.MuzeZapisovatVeStavu) nově povolen i ve stavu UZAVRENO; stav ' +
      'operativy se zápisem nemění. PUT /api/polozky/{id} ve stavech SCHVALENO / V_REALIZACI / ' +
      'UZAVRENO projde, pokud se nemění popis činnosti (jinak 409), a zapíše se do historie. ' +
      'Poznámku operativy smí VEDOUCI/ADMIN měnit ve všech stavech mimo KE_SCHVALENI. ' +
      'Nový read-only endpoint GET /api/material/dny?od=&do=. Bez migrace. ' +
      'Nasazení: docker compose build api web.',
  },
  {
    date: '2026-09-30',
    title: 'Přehled spotřeby materiálu, oprava mizející mechanizace',
    items: [
      {
        kind: 'novinka',
        title: 'Nová stránka Materiál — kolik čeho se spotřebovalo',
        detail:
          'V menu přibyla stránka Materiál s přehledem spotřebovaného materiálu za zvolené ' +
          'období (výchozí je aktuální měsíc, rychlé volby Tento / Minulý měsíc). U každého ' +
          'materiálu vidíte celkové sečtené množství po jednotkách (např. „Kabel 120 m"), kolikrát ' +
          'byl zapsán a na kolika operativách. Počítá se podle data operativy. Zápisy, kde ' +
          'množství chybí nebo nejde o číslo s jednotkou (např. „cca 5 m"), se nesčítají a jsou ' +
          'uvedené zvlášť. Tabulku lze řadit podle množství, názvu nebo počtu výskytů.',
      },
      {
        kind: 'oprava',
        title: 'Při úpravě položky už nemizí vybraná mechanizace',
        detail:
          'Když se okno „Upravit položku" otevřelo dřív, než se stihl načíst seznam ' +
          'mechanizací, pole Mechanizace se občas vyprázdnilo a položku nešlo uložit ' +
          'bez nového výběru. Nově zůstane původní mechanizace vybraná vždy, i když ' +
          'je mezitím v číselníku vyřazená. Stejně se opravil výběr zástupce na stránce Zástupy.',
      },
    ],
    adminNote:
      'Čistě frontendová oprava (sdílená komponenta Select ignoruje falešný reset hodnoty na "" ' +
      'ze skrytého nativního selectu Radixu). Data v DB nebyla dotčena — mechanizace se ' +
      'na položkách nikdy nepřepsala, jen se vyprázdnil formulář. ' +
      'Stránka Materiál: nový read-only endpoint GET /api/material/spotreba?od=&do= (každý ' +
      'přihlášený, stejně jako čtení operativ; bez migrace). „Kolik" je volný text — sčítá se jen ' +
      'tvar „číslo + jednotka" (desetinná čárka/tečka, mezera jako oddělovač tisíců), období max. 2 roky. ' +
      'Nasazení: docker compose build api web.',
  },
  {
    date: '2026-09-21',
    title: 'Zástupcem schvalovatele může být i technik DPB',
    items: [
      {
        kind: 'vylepseni',
        title: 'Schvalovatel si může nastavit zástup i na technika naší firmy',
        detail:
          'Dosud šel zástup nastavit jen na jiného schvalovatele nebo administrátora. ' +
          'Nově lze jako zástupce vybrat kohokoliv z naší firmy včetně techniků. ' +
          'Zástupce po nastavenou dobu dostává e-maily ke schválení a na detailu operativy ' +
          'vidí tlačítka Schválit / Zamítnout u řádku zastoupeného schvalovatele. ' +
          'Rozhodnout může jen za toho, koho aktuálně zastupuje, a v řádku zůstane ' +
          'zapsáno, kdo skutečně rozhodl.',
      },
      {
        kind: 'vylepseni',
        title: 'Technik vidí, koho a kdy zastupuje',
        detail:
          'Technikům naší firmy se v menu objevila stránka Zástupy — jen pro čtení, ' +
          'zástup si nastavit nemohou. Na úvodní stránce se jim karta „Čeká na vaše ' +
          'schválení" zobrazí jen tehdy, když na ně jako na zástupce něco skutečně čeká.',
      },
    ],
    adminNote:
      'Nová policy CanRozhodovatSchvaleni (TECHNIK_INT, SCHVALOVATEL, ADMIN) na endpointech ' +
      'schvalit / zamitnout, /api/uzivatele/vyber a na celém /api/zastupy. Původní ' +
      'CanSchvalovat (SCHVALOVATEL, ADMIN) zůstává pro vlastní schvalovací řádky. Validace ' +
      'zástupce v ZastupService pouští role naší firmy; externí firma zástupcem být nemůže. ' +
      'Per-řádkové ověření (rozhoduje jen původní schvalovatel nebo aktivní zástup) ' +
      'beze změny. Bez migrace, stačí nasadit API i web.',
  },
  {
    date: '2026-08-24',
    title: 'Správné časy v PDF exportu',
    items: [
      {
        kind: 'oprava',
        title: 'Časy v PDF už nejsou posunuté o 2 hodiny',
        detail:
          'PDF export operativy zobrazoval časy (vytvořeno, uzavřeno, rozhodnutí ' +
          'schvalovatelů, čas vygenerování v patičce) ve světovém čase (UTC). ' +
          'Nově se všechny časy převádějí na český čas.',
      },
    ],
    adminNote:
      'Server ukládá časy v UTC a PDF se generuje na serveru — převod na ' +
      'Europe/Prague se nyní dělá explicitně ve formátovacím helperu PDF. ' +
      'Bez migrace, stačí nasadit API.',
  },
  {
    date: '2026-08-20',
    title: 'Uzavření operativy dokončí činnosti, čistší PDF',
    items: [
      {
        kind: 'vylepseni',
        title: 'Uzavření operativy automaticky označí naplánované činnosti jako provedené',
        detail:
          'Při uzavření operativy se u všech činností, které zůstaly ve stavu ' +
          '„Naplánováno", změní stav na „Provedeno". Činnosti ručně označené jako ' +
          '„Provedeno částečně" nebo „Neprovedeno" zůstávají beze změny.',
      },
      {
        kind: 'vylepseni',
        title: 'PDF export bez číslování činností',
        detail:
          'V PDF operativy už jednotlivé činnosti nemají před názvem pořadové číslo ' +
          '(1., 2., 3.…) — nadpis karty začíná rovnou kódem mechanizace a popisem.',
      },
    ],
  },
  {
    date: '2026-08-19',
    title: 'Zástupy schvalovatelů na dovolenou',
    items: [
      {
        kind: 'novinka',
        title: 'Nová stránka Zástupy — kdo za koho schvaluje v době nepřítomnosti',
        detail:
          'Každý schvalovatel si před dovolenou nastaví svého zástupce a období od–do ' +
          '(administrátor to může nastavit i změnit komukoliv). Po nastavenou dobu ' +
          'přechází na zástupce všechny povinnosti schvalovatele: vidí čekající ' +
          'schválení na nástěnce, může operativy schvalovat i zamítat a chodí mu ' +
          'e-mailové notifikace. Po skončení období se vše samo vrátí zastoupenému. ' +
          'Zástupce dostane při nastavení zástupu e-mail s obdobím a jménem zastoupeného.',
      },
      {
        kind: 'vylepseni',
        title: 'Hlídání kolizí zástupů',
        detail:
          'Aplikace nedovolí nastavit dva zástupy stejného schvalovatele s překrývajícím ' +
          'se obdobím, zástup sám za sebe ani zástupce, který nemá schvalovací roli — ' +
          'formulář rovnou vysvětlí proč.',
      },
    ],
    adminNote:
      'Nové endpointy /api/zastupy (GET/POST/PUT/DELETE + GET /moznosti), role ' +
      'SCHVALOVATEL/ADMIN; ne-admin smí spravovat jen vlastní zástupy (vynuceno na ' +
      'backendu). Zástupce musí mít roli SCHVALOVATEL nebo ADMIN. Změny zástupů se ' +
      'plně auditují (tabulka zastupy, historie_zmen). Nový typ notifikace ' +
      'zastup_nastaven. Bez migrace — tabulka zastupy existuje od začátku.',
  },
  {
    date: '2026-08-13',
    title: 'PDF denní operativy na jednu stránku + účastníci operativy',
    items: [
      {
        kind: 'vylepseni',
        title: 'PDF denní operativy se vždy vejde na jednu stránku A4',
        detail:
          'Export do PDF se nově automaticky přizpůsobí množství obsahu — když je toho ' +
          'na daný den víc, písmo se úměrně zmenší tak, aby celý záznam zůstal na jedné ' +
          'stránce. Zmenšily se i okraje, takže se na papír vejde víc textu.',
      },
      {
        kind: 'vylepseni',
        title: 'Nový nadpis a firma u autora záznamu',
        detail:
          'PDF má nově nadpis „Záznam z denní operativy" a u řádku Vytvořil se vedle ' +
          'jména zobrazuje i firma autora — Green Gas DPB, a.s., nebo ' +
          'TechnologyPipe Services s.r.o.',
      },
      {
        kind: 'novinka',
        title: 'Účastníci operativy — výběr při schválení a výpis v PDF',
        detail:
          'Schvalovatel při schvalování nově vybere ze seznamu uživatelů, kdo se ' +
          'operativy účastní. Vybraní účastníci se zobrazí na detailu operativy a ' +
          'vytisknou se na konci PDF záznamu. Když schvaluje víc schvalovatelů, ' +
          'každý může seznam doplnit — výběry se sčítají.',
      },
    ],
    adminNote:
      'Nová tabulka ucastnici_operativy (migrace se aplikuje automaticky při startu API). ' +
      'Nový endpoint GET /api/uzivatele/vyber (role SCHVALOVATEL/ADMIN) vrací jen id, ' +
      'jméno a stranu aktivních uživatelů. Firma v PDF se odvozuje ze strany účtu ' +
      '(interní = Green Gas DPB, a.s., externí = TechnologyPipe Services s.r.o.).',
  },
  {
    date: '2026-08-07',
    title: 'Výběr zaměstnanců z číselníku',
    items: [
      {
        kind: 'novinka',
        title: 'Zaměstnanci u položky operativy se vybírají z roletky',
        detail:
          'Místo ručního vypisování jména se u položky operativy nově vybírá jeden nebo ' +
          'více zaměstnanců z rozbalovacího seznamu. Když někdo v seznamu chybí, stačí ' +
          'jeho jméno napsat a rovnou z roletky přidat — příště už ho uvidí všichni.',
      },
      {
        kind: 'novinka',
        title: 'Nový číselník Zaměstnanci',
        detail:
          'V menu přibyla stránka Zaměstnanci, kde vedoucí a administrátoři udržují ' +
          'seznam osob nabízených u položek operativy. Zaměstnance lze přejmenovat nebo ' +
          'deaktivovat — deaktivovaný se přestane nabízet, na starých položkách ale ' +
          'jeho jméno zůstane.',
      },
    ],
    adminNote:
      'Nová tabulka zamestnanci (migrace AddZamestnanci se aplikuje při startu API), ' +
      'endpoint /api/zamestnanci — čtení pro přihlášené, zápis policy CanSpravovatCiselniky ' +
      '(VEDOUCI + ADMIN), basic audit log. Položka dál ukládá jména denormalizovaně do ' +
      'sloupce osoba (rozšířen na 1000 znaků, jména oddělená čárkou) — stará data i PDF ' +
      'fungují beze změny.',
  },
  {
    date: '2026-08-07',
    title: 'Uživatel může mít více rolí',
    items: [
      {
        kind: 'novinka',
        title: 'Jednomu účtu lze přiřadit více rolí najednou',
        detail:
          'Ve správě uživatelů se role nově vybírají zaškrtávátky, takže jeden účet ' +
          'může být třeba administrátor a zároveň schvalovatel — nepotřebujete kvůli ' +
          'tomu dva účty. Kombinovat lze jen role stejné strany (Green Gas DPB, nebo ' +
          'externí firma); v tabulce uživatelů se každá role zobrazí vlastním štítkem.',
      },
    ],
    adminNote:
      'Backend nově vrací 400 (neplatna_kombinace_roli) při pokusu smíchat role obou ' +
      'stran na jednom účtu; při změně rolí se strana účtu synchronizuje podle rolí. ' +
      'Bez migrace — tabulka user_roles byla M:N už dřív.',
  },
  {
    date: '2026-08-05',
    title: 'Opravy formátování v PDF',
    items: [
      {
        kind: 'oprava',
        title: 'Popis položky se v PDF tiskl celý tučně',
        detail:
          'V denní operativě se popis činnosti vytiskl tučně i tam, kde tučný nebyl — ' +
          'dědil se formát nadpisu položky. Nově se v PDF tiskne přesně to, co je vidět ' +
          'v editoru: tučné jen tam, kde jste tučné písmo použili.',
      },
      {
        kind: 'oprava',
        title: 'Kurzíva se do PDF nepropisovala',
        detail:
          'Text psaný kurzívou vycházel v PDF jako běžné písmo. Teď se vytiskne skloněný, ' +
          'takže i kurzíva v operativě odpovídá tomu, co je v aplikaci.',
      },
    ],
    adminNote:
      'Kurzívu řeší simulace v PdfSharp (EmbeddedFontResolver žádá ItalicSimulation) — ' +
      'embedded Segoe WP nemá italic řez. Vyžaduje rebuild API image.',
  },
  {
    date: '2026-08-04',
    title: 'Formátování textu v popisech a poznámkách',
    items: [
      {
        kind: 'vylepseni',
        title: 'Tučné písmo, kurzíva, podtržení a odrážky',
        detail:
          'Všechna delší textová pole mají nad sebou lištu s formátováním: tučně, kurzíva, ' +
          'podtrženě, odrážky a číslovaný seznam (plus tlačítko, které formátování zruší). ' +
          'Fungují i klávesové zkratky Ctrl+B, Ctrl+I a Ctrl+U. Týká se to popisu činnosti ' +
          'v plánu i v číselníku, poznámky operativy a celého reportu položky — výkonu práce, ' +
          'provedených úkonů, zjištění a kontroly. Důležité věci tak jde v dlouhém popisu ' +
          'zvýraznit a postup rozepsat do bodů.',
      },
      {
        kind: 'vylepseni',
        title: 'Předvolby činností vloží název i popis',
        detail:
          'V dialogu položky operativy tlačítka s kódy činností nově vloží do popisu ' +
          'název činnosti tučně a pod něj celý popis z číselníku — dřív se přepsal jen ' +
          'název. Kliknutím na další činnost se blok připojí pod ten předchozí, takže ' +
          'jde poskládat víc činností do jedné položky.',
      },
      {
        kind: 'vylepseni',
        title: 'Formátování se propíše i do PDF',
        detail:
          'Stažená denní operativa vypadá stejně jako v aplikaci — tučné a podtržené části ' +
          'zůstanou zvýrazněné a odrážky se vytisknou jako seznam. Starší zápisy bez ' +
          'formátování se zobrazují a tisknou přesně jako dřív, nic se nemusí přepisovat.',
      },
    ],
    adminNote:
      'Formátovaná pole se ukládají jako HTML omezené na strong/em/u/br/p/ul/ol/li; whitelist ' +
      'vynucuje i backend (Dom.Api.Infrastructure.RichText, balíček HtmlSanitizer), takže ani ' +
      'volání API mimo UI nedostane do DB nic jiného. Migrace DB není potřeba — hodnoty bez ' +
      'tagů se berou jako plain text a převádějí se na odstavce až při zápisu. Frontend přidává ' +
      'závislost TipTap, takže je nutný docker compose build web.',
  },
  {
    date: '2026-08-03',
    title: 'Stránka Novinky',
    items: [
      {
        kind: 'novinka',
        title: 'Přehled změn přímo v aplikaci',
        detail:
          'V levém menu přibyla položka Novinky — časová osa toho, co se v aplikaci mění, nejnovější nahoře. Změny jsou popsané jazykem uživatele (co je nově vidět a k čemu to je), dají se filtrovat na Nové / Vylepšení / Opravy. Není tedy nutné se ptát, co se změnilo po posledním nasazení.',
      },
    ],
    adminNote:
      'Zdrojem je Frontend/dom-web/src/content/changelog.ts — z něj se plní stránka /novinky i root ' +
      'CHANGELOG.md (generuje node scripts/gen-changelog.mjs, ručně se needituje). Stránku vidí každý ' +
      'přihlášený, poznámky „Pro správce" jen role ADMIN. Frontend se musí přebuildit ' +
      '(docker compose build web), jinak zůstane v obrazu stará verze.',
  },
  {
    date: '2026-08-03',
    title: 'Číselníky spravuje i vedoucí',
    items: [
      {
        kind: 'oprava',
        title: 'Vedoucí externí firmy může upravovat mechanizaci i činnosti',
        detail:
          'Zakládání, úprava i vyřazení záznamu v číselnících Mechanizace a Činnosti dřív končilo hláškou o nedostatečném oprávnění a spravovat je mohl jen administrátor. Nově to zvládne i vedoucí — číselníky si podle zadání spravují obě strany. Oba seznamy si dál zobrazí každý přihlášený; technik je jen čte a tlačítka má neaktivní s vysvětlením proč.',
      },
    ],
    adminNote:
      'Zápisové akce MechanizaceController i CinnostiController (POST/PUT/DELETE) přešly z policy ' +
      'IsAdmin na novou CanSpravovatCiselniky (VEDOUCI + ADMIN) — záměrně jedna policy pro oba ' +
      'číselníky; frontend to zrcadlí přes perms.canSpravovatCiselniky. ' +
      'Log odepřených akcí (403) je nově podrobnější — obsahuje operaci (controller.akce), vyžadovanou ' +
      'policy, důvod odmítnutí a stranu uživatele; oba případy (policy i doménová 403) mají prefix ' +
      '„Neopravnena akce", takže se grepnou jedním vzorem. Viz TROUBLESHOOTING.md #14. ' +
      'Pozor: skryté tlačítko na frontendu = žádný request = nic v logu, proto se zakázané akce ' +
      'gateují neaktivním tlačítkem s důvodem, ne skrytím.',
  },
  {
    date: '2026-07-30',
    title: 'E-maily z firemního účtu Microsoft 365',
    items: [
      {
        kind: 'vylepseni',
        title: 'Notifikace chodí ze servisní firemní schránky',
        detail:
          'E-maily o schválení, zamítnutí, uzavření dne, týdenním souhrnu i reset hesla se odesílají přes Microsoft 365 firemním kanálem, ne přes obecný SMTP účet s heslem. Pro příjemce to znamená, že zprávy chodí spolehlivěji a nekončí ve spamu.',
      },
    ],
    adminNote:
      'Vedle SMTP (MailKit) je nově sender přes Microsoft Graph — přepíná se konfigurací, viz ' +
      'docs/office365-graph-mail.md; varianta SMTP s OAuth2 (XOAUTH2) je v docs/office365-oauth2.md. ' +
      'Bez konfigurace dispatcher jen loguje warning a queue se nehromadí. Otestování z příkazové řádky: ' +
      'scripts/test-graph-mail.ps1.',
  },
  {
    date: '2026-07-30',
    title: 'Přihlášení firemním účtem (Office 365 SSO)',
    items: [
      {
        kind: 'novinka',
        title: 'Přihlášení přes firemní účet',
        detail:
          'Vedle e-mailu a hesla se lze přihlásit firemním účtem Microsoft 365 — bez zakládání dalšího hesla. Účet se při prvním přihlášení napojí na existujícího uživatele podle e-mailu, role a oprávnění zůstávají stejné.',
      },
    ],
    adminNote:
      'Manual OpenIdConnect flow (Microsoft.Identity.Web), account linking přes User.AzureObjectId. ' +
      'Bez sekce AzureAd v konfiguraci je SSO vypnuté a přihlášení jede jen lokálně. Postup zprovoznění ' +
      'včetně Redirect URI: docs/office365-sso.md.',
  },
  {
    date: '2026-07-29',
    title: 'Ostrý provoz na firemním serveru',
    items: [
      {
        kind: 'novinka',
        title: 'Aplikace běží na firemní adrese přes HTTPS',
        detail:
          'Denní operativa už neběží jen na testovacím počítači — je na firemním serveru, dostupná přes zabezpečené spojení. Nasazení nové verze probíhá automaticky, takže opravy jsou k dispozici rychleji.',
      },
    ],
    adminNote:
      'Deploy přes SSH z GitLab CI, artefakt se bere z registru (nerebuilduje se na serveru), ' +
      'Traefik + Let’s Encrypt pro TLS. Konfigurace v docker-compose.prod.yml a .env.prod.example, ' +
      'kompletní postup v DEPLOYMENT.md.',
  },
  {
    date: '2026-06-03',
    title: 'Zjištění pro schvalovatele, poznámka pro techniky',
    items: [
      {
        kind: 'oprava',
        title: 'Schvalovatel může zapsat „Zjištění"',
        detail:
          'Pole Zjištění šlo v položce otevřít, ale uložení skončilo hláškou o nedostatečném oprávnění. Nově schvalovatel zjištění zapíše, jak zadání předpokládá.',
      },
      {
        kind: 'vylepseni',
        title: 'Technik externí firmy může upravit poznámku operativy',
        detail:
          'Poznámku k dennímu plánu lze doplnit i v průběhu realizace (stavy Schváleno / V realizaci / Upravena), ne jen před schválením. Tlačítko „Upravit / Přidat" se navíc nabízí jen tomu, kdo poznámku opravdu smí měnit — dřív se zobrazovalo všem a akce pak skončila chybou.',
      },
      {
        kind: 'oprava',
        title: 'Odkazy v e-mailech vedou na správnou adresu',
        detail:
          'Notifikace obsahovaly odkaz na vývojovou adresu (localhost), který příjemci nefungoval. Odkaz nyní vede na adresu, kde aplikace skutečně běží.',
      },
    ],
    adminNote:
      'Odkazy se skládají z APP_HOST (.env) — bez něj míří na dev localhost. Zjištění: doplněn ' +
      'SCHVALOVATEL do ReportField.Zjisteni. Poznámka: samostatná policy CanEditPoznamka + ' +
      'OperativaService.MuzeEditovatPoznamku (role × stav), FE zrcadlo perms.canEditPoznamka; ' +
      'CanEditNavrh záměrně nerozšířena — kryje i smazat/odeslat/uzavřít. Odepřené akce (403) se ' +
      'nově logují do file logu jako Error (ne do auditu).',
  },
  {
    date: '2026-06-01',
    title: 'Správa hesel a bezpečnější přihlášení',
    items: [
      {
        kind: 'novinka',
        title: 'Administrátor umí nastavit nebo resetovat heslo',
        detail:
          'U existujícího uživatele lze poslat odkaz pro nastavení hesla, nebo heslo zadat přímo. Obě akce se zapisují do auditu (bez samotného hesla), takže je dohledatelné, kdo komu heslo měnil.',
      },
      {
        kind: 'novinka',
        title: 'Požadavky na sílu hesla',
        detail:
          'Heslo musí mít 8–128 znaků a alespoň tři ze čtyř druhů znaků (malé, velké, číslice, speciální); triviální hesla a heslo obsahující vlastní e-mail aplikace odmítne. Nesplněný požadavek se ukáže hned při psaní, ne až po odeslání.',
      },
      {
        kind: 'vylepseni',
        title: 'Profil se změnou hesla',
        detail:
          'Stránka Profil ukazuje e-mail a jméno (mění je administrátor) a umožňuje změnit si vlastní heslo. Odkaz na Profil je i v nabídce pod jménem vpravo nahoře.',
      },
      {
        kind: 'oprava',
        title: 'Změna role uživatele už nekončí chybou',
        detail:
          'Uložení jiné role v seznamu uživatelů padalo na chybu serveru. Kromě opravy nově aplikace nedovolí odebrat si vlastní oprávnění ani se sám deaktivovat — nešlo by se pak dostat zpátky.',
      },
      {
        kind: 'oprava',
        title: 'Tlačítka Schválit / Zamítnout jen tam, kde dávají smysl',
        detail:
          'V přehledu schvalování se akce nabízely i u řádků, o kterých přihlášený nerozhoduje.',
      },
      {
        kind: 'vylepseni',
        title: 'Větší pole pro popis činnosti',
        detail:
          'Dialogy pro zadání popisu jsou širší a textové pole vyšší — delší popis je vidět celý bez rolování.',
      },
    ],
    adminNote:
      'Zdroj pravdy pro politiku hesla je backend (PasswordPolicy, porušení => HTTP 400 slabe_heslo); ' +
      'frontend ji zrcadlí v src/lib/passwordPolicy.ts. Password akce se logují přes ' +
      'IHistorieLogger.LogAkceAsync (reset_hesla / zmena_hesla). Self-edit guard vrací 409. ' +
      'Reset-linky se enqueuují do notifikační fronty — bez nakonfigurovaného odesílání se neodešlou. ' +
      'Detail k chybě změny rolí: TROUBLESHOOTING.md #12.',
  },
  {
    date: '2026-05-29',
    title: 'Operativy po týdnech a ovládání na mobilu',
    items: [
      {
        kind: 'novinka',
        title: 'Seznam operativ seskupený po týdnech',
        detail:
          'Operativy se zobrazují po kalendářních týdnech v mřížce Po–Pá, s rychlými filtry na tento a minulý týden. U každého týdne je akce „Uzavřít týden" a „PDF týdne" — není nutné procházet dny po jednom.',
      },
      {
        kind: 'novinka',
        title: 'Ovládání na telefonu a tabletu',
        detail:
          'Menu se na malé obrazovce skládá pod tlačítko a široké tabulky jdou posouvat do stran, takže aplikace jde použít i v terénu.',
      },
      {
        kind: 'vylepseni',
        title: 'Tlačítko PDF v detailu operativy funguje',
        detail:
          'Stažení denní operativy jako PDF bylo dosud neaktivní.',
      },
      {
        kind: 'vylepseni',
        title: 'Sjednocené názvosloví — „mechanizace" a „Green Gas DPB a.s."',
        detail:
          'V celé aplikaci je jednotně „mechanizace" (dřív i „mechanizmus") a místo obecného „naše firma" se uvádí Green Gas DPB a.s.',
      },
      {
        kind: 'vylepseni',
        title: 'Tlačítko „Otevřít" místo ikony oka',
        detail:
          'V tabulce položek je pojmenované tlačítko — je jasné, co udělá.',
      },
    ],
    adminNote:
      'Nový endpoint POST /api/operativy/uzavrit-tyden?tydenOd=. Za reverse proxy je potřeba ' +
      'UseForwardedHeaders a cookie SameAsRequest (jinak přihlášení v produkci padá). ' +
      'Serilog píše Warning+ do ./log/dom-api-{date}.log, produkční data i logy jedou přes bind mounty ' +
      'vedle docker-compose.yml.',
  },
  {
    date: '2026-05-28',
    title: 'První verze aplikace Denní operativa',
    items: [
      {
        kind: 'novinka',
        title: 'Denní operativa montáží',
        detail:
          'Plán na den se zakládá, kopíruje z předchozího dne a doplňuje o položky (mechanizace, popis, osoba). U položky se zapisuje výkon, úkony, materiál, zjištění a kontrola; stav položky je Naplánováno / Provedeno / Provedeno částečně / Neprovedeno.',
      },
      {
        kind: 'novinka',
        title: 'Schvalovací proces',
        detail:
          'Operativa prochází stavy Návrh → Ke schválení → Schváleno → V realizaci → Uzavřeno, s možností otevřít ji zpět k úpravě. Schvalují všichni aktivní schvalovatelé; pokud je někdo nepřítomen, úkol dostane jeho zástup.',
      },
      {
        kind: 'novinka',
        title: 'E-mailové notifikace a týdenní souhrn',
        detail:
          'O odeslání ke schválení, schválení, zamítnutí, úpravě po schválení i uzavření dne chodí e-mail těm, koho se to týká. V pondělí ráno navíc odchází souhrn za předchozí týden v PDF.',
      },
      {
        kind: 'novinka',
        title: 'PDF denní operativy a týdenního souhrnu',
        detail:
          'Tiskový výstup s hlavičkou, schválením, položkami, materiálem a kontrolou — včetně české diakritiky.',
      },
      {
        kind: 'novinka',
        title: 'Kdo co má vidět — role a strany',
        detail:
          'Oprávnění se řídí rolí (administrátor, schvalovatel, vedoucí, technik interní / externí) a stranou (Green Gas DPB / externí firma). Každý vidí a edituje jen to, co mu přísluší — kontrolu například nikdy nezapisuje externí firma.',
      },
      {
        kind: 'novinka',
        title: 'Historie změn (audit)',
        detail:
          'U operativ, položek, materiálu, schválení a zástupů je dohledatelné, kdo a kdy co změnil, včetně hodnot před a po. Administrátor má samostatnou stránku Audit s filtry, u konkrétního záznamu jde historie otevřít přímo z detailu.',
      },
      {
        kind: 'novinka',
        title: 'Číselníky, uživatelé a našeptávače',
        detail:
          'Správa mechanizace a činností, správa uživatelů a jejich rolí, přihlášení e-mailem s možností obnovy zapomenutého hesla. Při psaní materiálu a jména osoby aplikace našeptává dříve použité hodnoty.',
      },
      {
        kind: 'novinka',
        title: 'Přehled na úvodní stránce',
        detail:
          'Dashboard podle role: „Čeká na vaše schválení", dnešní operativa s odkazem na založení, přehled aktuálního týdne Po–Pá a rychlé odkazy.',
      },
    ],
    adminNote:
      'Stack: ASP.NET Core 9 + EF Core 9 + PostgreSQL 16 + React 18/Vite; běží v Dockeru ' +
      '(docker compose up -d --build), web :8180, API :5180. Migrace se aplikují na startu API. ' +
      'Výchozí účet admin@dpb.cz / admin — po nasazení změnit. Rozsah a architektura: STATE.md, ' +
      'ARCHITECTURE.md, docs/PROJEKT.md.',
  },
];
